import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Shape API de un archivo digital de sucursal. */
export interface BranchDigitalFileDto {
  id: string;
  branchId?: string;
  name: string;
  validityDate?: string | null;
  noExpiration?: boolean;
  fileUrl?: string;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type BranchDigitalFileWritePayload = {
  name: string;
  noExpiration: boolean;
  validityDate?: string | null;
};

export type BranchDigitalFileListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Archivo digital de sucursal.
 *   GET    /api/structure/branches/{branchId}/digital-files
 *   POST   /api/structure/branches/{branchId}/digital-files
 *   PUT    /api/structure/branches/{branchId}/digital-files/{id}
 *   PATCH  /api/structure/branches/{branchId}/digital-files/{id}/status
 *   DELETE /api/structure/branches/{branchId}/digital-files/{id}
 */
@Injectable({ providedIn: 'root' })
export class BranchDigitalFileService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(branchId: string): string {
    return `${this.apiConfig.apiUrl}/structure/branches/${branchId}/digital-files`;
  }

  list(
    branchId: string,
    params: BranchDigitalFileListParams = {},
  ): Observable<BranchDigitalFileDto[]> {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 100))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<BranchDigitalFileDto[] | PageableDto<BranchDigitalFileDto>>
      >(this.resource(branchId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  create(
    branchId: string,
    payload: BranchDigitalFileWritePayload,
    file: File,
  ): Observable<BranchDigitalFileDto> {
    return this.http
      .post<ApiResponse<BranchDigitalFileDto>>(
        this.resource(branchId),
        this.toFormData(payload, file),
      )
      .pipe(map((res) => res.data as BranchDigitalFileDto));
  }

  update(
    branchId: string,
    fileId: string,
    payload: BranchDigitalFileWritePayload,
    file?: File | null,
  ): Observable<BranchDigitalFileDto> {
    return this.http
      .put<ApiResponse<BranchDigitalFileDto>>(
        `${this.resource(branchId)}/${fileId}`,
        this.toFormData(payload, file),
      )
      .pipe(map((res) => res.data as BranchDigitalFileDto));
  }

  updateStatus(
    branchId: string,
    fileId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(branchId)}/${fileId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(branchId: string, fileId: string): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(branchId)}/${fileId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Archivo eliminado exitosamente'),
        ),
      );
  }

  private toFormData(
    payload: BranchDigitalFileWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('noExpiration', String(payload.noExpiration));
    if (!payload.noExpiration && payload.validityDate) {
      formData.append('validityDate', payload.validityDate);
    }
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | BranchDigitalFileDto[]
      | PageableDto<BranchDigitalFileDto>
      | null
      | undefined,
  ): BranchDigitalFileDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
